import { formatNumber } from '../geometry/path';
import { STROKE_LIMITS } from '../model/defaults';
import type { Design, NailInstance, ProjectDocument } from '../model/types';
import { evaluateDesign, evaluateNail, type EvaluatedPath } from '../scene/evaluate';
import type { ResolvedFill } from '../scene/fill';
import type { ResolvedStroke } from '../scene/stroke';
import { escapeXml } from './xml';

/**
 * Trusted SVG output (docs/06, docs/07). The export vocabulary is deliberately small:
 * `svg`, `title`, `desc`, `defs`, `clipPath`, `g`, and `path`: line art (`fill="none"`
 * inherited from the root) and filled shapes with solid colors (ADR 0016). Geometry is
 * baked into world coordinates so the resolved millimeter stroke widths never depend on
 * viewer `vector-effect` support. No raster images, fonts, scripts, styles, gradients, or
 * preview-only layers (polish, gloss, skin, guides, selection) can appear: this module
 * never receives them.
 */
export type InkMode = 'stencil-black' | 'design-colors';

export const STENCIL_INK = '#000000';
export const NAIL_OUTLINE_WIDTH_MM = 0.1;

export interface ExportWarning {
  readonly code: 'white-ink' | 'thin-line' | 'empty' | 'outside-page';
  readonly message: string;
}

export interface SvgDocument {
  readonly svg: string;
  readonly widthMm: number;
  readonly heightMm: number;
  readonly warnings: readonly ExportWarning[];
}

export interface ArtworkOptions {
  readonly ink: InkMode;
  readonly precision?: number;
}

export type SvgAttribute = readonly [name: string, value: string];

export interface PaintOptions {
  /** One color for all paint (stencil ink, thumbnails) instead of each path's own. */
  readonly ink?: string;
  /** Ignore opacity: stencils print solid ink. */
  readonly solid?: boolean;
  /** Decimal places for line widths. */
  readonly precision?: number;
  /** A line width to draw instead of the path's own (thumbnails thicken lines). */
  readonly strokeWidthMm?: number;
}

/**
 * `fill`, `fill-rule`, and `fill-opacity` of a filled shape; nothing for line art, which
 * inherits `fill="none"` from the SVG root or its group.
 */
export function fillAttributes(
  fill: ResolvedFill | null,
  options: PaintOptions = {},
): SvgAttribute[] {
  if (!fill) return [];
  const attrs: SvgAttribute[] = [['fill', options.ink ?? fill.color]];
  if (fill.rule === 'evenodd') attrs.push(['fill-rule', 'evenodd']);
  if (!options.solid && fill.opacity < 1)
    attrs.push(['fill-opacity', formatNumber(fill.opacity, 3)]);
  return attrs;
}

/** The line along a path, or `stroke="none"` for a filled shape without one. */
export function strokeAttributes(
  stroke: ResolvedStroke | null,
  options: PaintOptions = {},
): SvgAttribute[] {
  if (!stroke) return [['stroke', 'none']];
  const attrs: SvgAttribute[] = [
    ['stroke', options.ink ?? stroke.color],
    ['stroke-width', formatNumber(options.strokeWidthMm ?? stroke.widthMm, options.precision ?? 4)],
    ['stroke-linecap', stroke.lineCap],
    ['stroke-linejoin', stroke.lineJoin],
  ];
  if (!options.solid && stroke.opacity < 1) {
    attrs.push(['stroke-opacity', formatNumber(stroke.opacity, 3)]);
  }
  return attrs;
}

/** Every paint attribute of an evaluated path: its fill, then its line. */
export function paintAttributes(
  path: Pick<EvaluatedPath, 'fill' | 'stroke'>,
  options: PaintOptions = {},
): SvgAttribute[] {
  return [...fillAttributes(path.fill, options), ...strokeAttributes(path.stroke, options)];
}

export function attributesText(attributes: readonly SvgAttribute[]): string {
  return attributes.map(([name, value]) => ` ${name}="${value}"`).join('');
}

/** Renders evaluated paths as `<path>` elements with their lines and fills. */
export function renderPaths(paths: readonly EvaluatedPath[], options: ArtworkOptions): string {
  const stencil = options.ink === 'stencil-black';
  const paint: PaintOptions = {
    ...(stencil ? { ink: STENCIL_INK, solid: true } : {}),
    precision: options.precision ?? 4,
  };
  return paths
    .filter((path) => path.segments.length > 0)
    .map((path) => `<path d="${path.d}"${attributesText(paintAttributes(path, paint))}/>`)
    .join('');
}

export function collectArtworkWarnings(
  paths: readonly EvaluatedPath[],
  ink: InkMode,
): ExportWarning[] {
  const warnings: ExportWarning[] = [];
  if (paths.length === 0) {
    warnings.push({ code: 'empty', message: 'There is no visible art to export.' });
  }
  const widths = paths.flatMap((p) => (p.stroke ? [p.stroke.widthMm] : []));
  const thinnest = Math.min(...widths);
  if (widths.length > 0 && thinnest < STROKE_LIMITS.printWarningBelowMm) {
    warnings.push({
      code: 'thin-line',
      message: `Some lines are ${formatNumber(thinnest, 3)} mm wide. Lines thinner than ${STROKE_LIMITS.printWarningBelowMm} mm may not print clearly on every printer.`,
    });
  }
  const light = (color: string) => relativeLuminance(color) > 0.85;
  if (
    ink === 'design-colors' &&
    paths.some((p) => (p.stroke && light(p.stroke.color)) || (p.fill && light(p.fill.color)))
  ) {
    warnings.push({
      code: 'white-ink',
      message:
        'Some lines or shapes are white or very light and may be invisible on white paper. Use black stencil ink to print them.',
    });
  }
  return warnings;
}

export interface SvgFrame {
  readonly widthMm: number;
  readonly heightMm: number;
  readonly title: string;
  readonly description: string;
  readonly defs?: string;
  readonly body: string;
}

/** Wraps content in an SVG root whose user unit is exactly one millimeter. */
export function wrapSvg(frame: SvgFrame): string {
  const w = formatNumber(frame.widthMm, 4);
  const h = formatNumber(frame.heightMm, 4);
  return [
    '<?xml version="1.0" encoding="UTF-8"?>',
    `<svg xmlns="http://www.w3.org/2000/svg" version="1.1" width="${w}mm" height="${h}mm" viewBox="0 0 ${w} ${h}" fill="none">`,
    `<title>${escapeXml(frame.title)}</title>`,
    `<desc>${escapeXml(frame.description)}</desc>`,
    frame.defs ? `<defs>${frame.defs}</defs>` : '',
    frame.body,
    '</svg>',
    '',
  ].join('');
}

/** Stable identifier fragment for generated clip paths (no cross-artboard collisions). */
export function clipId(scope: string, index: number): string {
  const safe = scope.replace(/[^a-zA-Z0-9-]/g, '-').slice(0, 48);
  return `jnp-clip-${safe}-${index}`;
}

/** Artboard scope: the active design at its artboard size, not clipped to a nail (docs/07). */
export function exportDesignSvg(
  design: Design,
  options: ArtworkOptions & { readonly projectName?: string },
): SvgDocument {
  const scene = evaluateDesign(design, { precision: options.precision ?? 4 });
  const { widthMm, heightMm } = design.artboard;
  const svg = wrapSvg({
    widthMm,
    heightMm,
    title: `${options.projectName ?? 'Jamie’s Nails Pro'} — ${design.name}`,
    description: `Vector art, ${formatNumber(widthMm, 2)} × ${formatNumber(heightMm, 2)} mm artboard. Exported by Jamie’s Nails Pro. Print at Actual size / 100%.`,
    body: `<g fill="none">${renderPaths(scene.paths, options)}</g>`,
  });
  return { svg, widthMm, heightMm, warnings: collectArtworkWarnings(scene.paths, options.ink) };
}

/** Nail scope: the nail's design through its placement, clipped to its silhouette. */
export function exportNailSvg(
  doc: ProjectDocument,
  nail: NailInstance,
  options: ArtworkOptions & { readonly includeOutline: boolean },
): SvgDocument {
  const evaluated = evaluateNail(doc, nail, { precision: options.precision ?? 4 });
  if (!evaluated) throw new Error(`Nail ${nail.id} references a missing design.`);
  const id = clipId(nail.id, 0);
  const outline = options.includeOutline
    ? `<path d="${evaluated.silhouetteD}" stroke="${STENCIL_INK}" stroke-width="${NAIL_OUTLINE_WIDTH_MM}" stroke-linejoin="round"/>`
    : '';
  const svg = wrapSvg({
    widthMm: nail.widthMm,
    heightMm: nail.lengthMm,
    title: `${doc.name} — ${nail.hand} ${nail.finger} nail`,
    description: `Vector art clipped to a ${nail.shape.id} nail, ${formatNumber(nail.widthMm, 2)} × ${formatNumber(nail.lengthMm, 2)} mm. Exported by Jamie’s Nails Pro. Print at Actual size / 100%.`,
    defs: `<clipPath id="${id}"><path d="${evaluated.silhouetteD}"/></clipPath>`,
    body: `<g clip-path="url(#${id})" fill="none">${renderPaths(evaluated.scene.paths, options)}</g>${outline}`,
  });
  return {
    svg,
    widthMm: nail.widthMm,
    heightMm: nail.lengthMm,
    warnings: collectArtworkWarnings(evaluated.scene.paths, options.ink),
  };
}

/** WCAG relative luminance of a #RRGGBB color (0 = black, 1 = white). */
export function relativeLuminance(hex: string): number {
  const channel = (i: number) => {
    const v = parseInt(hex.slice(i, i + 2), 16) / 255;
    return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * channel(1) + 0.7152 * channel(3) + 0.0722 * channel(5);
}
